import { FC, useCallback, useState } from "react";
import { FlatList, StyleSheet, View } from "react-native";
import { useSelector } from "react-redux";
import { kColors } from "src/kColors";
import { tCellData, tHandleColumnSortProps, tRenderCellProps, tSortConfig, tTableData, tTableProps } from "./types";
import { EmptyTableMessage, HeaderBig, HeaderSmall, RenderCard, RenderCell, RenderRow, sortColumn } from "./util";

const Table: FC<tTableProps> = ({
  headerData,
  tableData,
  onRowPress,
  shadeAlternates,
  loading,
  badgeColumnConfigs,
  emptyTableMessage,
}) => {
  const isSmall = useSelector((state) => state.NavigationReducer.drawerType === "slide");
  const [sCurrentSortConfig, _setCurrentSortConfig] = useState<tSortConfig>(null);
  const aTableData: tTableData = sortColumn(tableData, sCurrentSortConfig?.columnIndex, sCurrentSortConfig?.ascending);

  const _handleExtractRowKeys = useCallback((aRow) => {
    return aRow.join(", ");
  }, []);

  const _handleOnRowPress = useCallback(
    (myItem: Array<tCellData>, myIndex: number, myCellIndex: number) => {
      if (onRowPress) onRowPress(myIndex, myItem, myCellIndex);
    },
    [onRowPress],
  );

  const _handleRenderCell = useCallback(
    ({ myCellData, myCellIndex, isCard, badgeColumnConfigs: myBadgeColumnConfigs }: tRenderCellProps) => (
      <RenderCell
        myCellData={myCellData}
        myCellIndex={myCellIndex}
        isCard={isCard}
        badgeColumnConfigs={myBadgeColumnConfigs || []}
      />
    ),
    [],
  );

  const _handleColumnSort = useCallback(
    ({ myIndex, isAscending }: tHandleColumnSortProps) => {
      if (isAscending !== undefined) {
        _setCurrentSortConfig({
          columnIndex: myIndex,
          ascending: isAscending,
        });
      } else {
        if (sCurrentSortConfig?.columnIndex === myIndex) {
          if (sCurrentSortConfig.ascending) {
            _setCurrentSortConfig({
              columnIndex: myIndex,
              ascending: false,
            });
          } else {
            _setCurrentSortConfig({
              columnIndex: myIndex,
              ascending: true,
            });
          }
        } else {
          _setCurrentSortConfig({
            columnIndex: myIndex,
            ascending: true,
          });
        }
      }
    },
    [sCurrentSortConfig],
  );

  const _handleRenderHeader: FC = useCallback(
    () =>
      !isSmall ? (
        <HeaderBig
          headerData={headerData}
          _handleColumnSort={_handleColumnSort}
          currentSortConfig={sCurrentSortConfig}
        />
      ) : null,
    [isSmall, _handleColumnSort, headerData, sCurrentSortConfig],
  );

  const _handleRenderItem = useCallback(
    ({ item, index }: { item: tCellData[]; index: number }) => {
      if (isSmall)
        return (
          <RenderCard
            item={item}
            index={index}
            loading={loading || false}
            onRowPress={onRowPress}
            shadeAlternates={shadeAlternates}
            headerData={headerData}
            RenderCell={_handleRenderCell}
            _handleOnRowPress={_handleOnRowPress}
            badgeColumnConfigs={badgeColumnConfigs || []}
          />
        );
      return (
        <RenderRow
          item={item}
          index={index}
          loading={loading || false}
          onRowPress={onRowPress}
          shadeAlternates={shadeAlternates}
          headerData={headerData}
          RenderCell={_handleRenderCell}
          _handleOnRowPress={_handleOnRowPress}
          badgeColumnConfigs={badgeColumnConfigs || []}
        />
      );
    },
    [
      isSmall,
      loading,
      onRowPress,
      shadeAlternates,
      headerData,
      _handleOnRowPress,
      _handleRenderCell,
      badgeColumnConfigs,
    ],
  );

  const _handleEmptyListComponent = useCallback(
    () => <EmptyTableMessage myMessage={emptyTableMessage || "There is no data to display"} />,
    [emptyTableMessage],
  );

  return (
    <View style={styles.mTableContainer}>
      {isSmall && (
        <HeaderSmall
          headerData={headerData}
          _handleColumnSort={_handleColumnSort}
          currentSortConfig={sCurrentSortConfig}
        />
      )}
      <FlatList
        style={styles.flexOne}
        ListHeaderComponent={_handleRenderHeader}
        ListEmptyComponent={_handleEmptyListComponent}
        data={aTableData}
        renderItem={_handleRenderItem}
        keyExtractor={_handleExtractRowKeys}
        scrollEnabled={false}
      />
    </View>
  );
};

export default Table;

const styles = StyleSheet.create({
  flexOne: {
    flex: 1,
  },
  mTableContainer: {
    backgroundColor: kColors.white,
    flex: 1,
  },
});
